import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  limit,
  getDocs,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import { db } from '@/lib/firebase/config'
import { createAuditLog } from './audit'
import type { Notification, UserRole } from '@/types'

// ============================================
// TYPES
// ============================================

interface CreateNotificationParams {
  userId: string
  agencyId: string
  type: Notification['type']
  title: string
  body: string
  relatedEntity?: Notification['relatedEntity']
  actionUrl?: string
  deliveryChannels?: ('in_app' | 'email' | 'sms' | 'push')[]
  expiresAt?: Date
}

interface BulkNotificationParams {
  userIds: string[]
  agencyId: string
  type: Notification['type']
  title: string
  body: string
  relatedEntity?: Notification['relatedEntity']
  actionUrl?: string
  deliveryChannels?: ('in_app' | 'email' | 'sms' | 'push')[]
  createdByUserId: string
  createdByEmail: string
  createdByRole: UserRole
}

// ============================================
// CREATE NOTIFICATION
// ============================================

export async function createNotification(
  params: CreateNotificationParams
): Promise<string> {
  const {
    userId,
    agencyId,
    type,
    title,
    body,
    relatedEntity,
    actionUrl,
    deliveryChannels = ['in_app'],
    expiresAt,
  } = params

  // Build notification data, only including optional fields if they have values
  const notificationData: Record<string, unknown> = {
    userId,
    agencyId,
    type,
    title,
    body,
    deliveryChannels,
    isRead: false,
    isDismissed: false,
    createdAt: serverTimestamp(),
  }

  // Only add optional fields if they have values
  if (relatedEntity) {
    notificationData.relatedEntity = relatedEntity
  }
  if (actionUrl) {
    notificationData.actionUrl = actionUrl
  }
  if (expiresAt) {
    notificationData.expiresAt = Timestamp.fromDate(expiresAt)
  }

  const docRef = await addDoc(collection(db, 'notifications'), notificationData)

  return docRef.id
}

// ============================================
// CREATE BULK NOTIFICATIONS
// ============================================

const NOTIFICATION_BATCH_LIMIT = 450

export async function createBulkNotifications(
  params: BulkNotificationParams
): Promise<string[]> {
  const {
    userIds,
    agencyId,
    type,
    title,
    body,
    relatedEntity,
    actionUrl,
    deliveryChannels = ['in_app'],
    createdByUserId,
    createdByEmail,
    createdByRole,
  } = params

  if (userIds.length === 0) return []

  const batch = writeBatch(db)
  const notificationIds: string[] = []

  for (const userId of userIds) {
    const docRef = doc(collection(db, 'notifications'))
    notificationIds.push(docRef.id)

    batch.set(docRef, {
      userId,
      agencyId,
      type,
      title,
      body,
      relatedEntity: relatedEntity || null,
      actionUrl: actionUrl || null,
      deliveryChannels,
      isRead: false,
      isDismissed: false,
      createdAt: serverTimestamp(),
      expiresAt: null,
    })
  }

  await batch.commit()

  // Log audit
  await createAuditLog({
    agencyId,
    userId: createdByUserId,
    userEmail: createdByEmail,
    userRole: createdByRole,
    action: 'create',
    resource: 'notification',
    resourceId: 'bulk',
    description: `Created ${userIds.length} notifications: ${title}`,
  })

  return notificationIds
}

// ============================================
// MARK AS READ
// ============================================

export async function markNotificationAsRead(
  notificationId: string,
  _agencyId: string
): Promise<void> {
  const docRef = doc(db, 'notifications', notificationId)

  await updateDoc(docRef, {
    isRead: true,
    readAt: serverTimestamp(),
  })
}

// ============================================
// MARK ALL AS READ
// ============================================

export async function markAllNotificationsAsRead(
  userId: string,
  agencyId: string
): Promise<number> {
  let totalUpdated = 0

  while (true) {
    const q = query(
      collection(db, 'notifications'),
      where('agencyId', '==', agencyId),
      where('userId', '==', userId),
      where('isRead', '==', false),
      limit(NOTIFICATION_BATCH_LIMIT)
    )

    const snapshot = await getDocs(q)
    if (snapshot.empty) break

    const batch = writeBatch(db)
    snapshot.docs.forEach(notificationDoc => {
      batch.update(notificationDoc.ref, {
        isRead: true,
        readAt: serverTimestamp(),
      })
    })

    await batch.commit()
    totalUpdated += snapshot.size

    if (snapshot.size < NOTIFICATION_BATCH_LIMIT) break
  }

  return totalUpdated
}

// ============================================
// DISMISS NOTIFICATION
// ============================================

export async function dismissNotification(
  notificationId: string,
  _agencyId: string
): Promise<void> {
  const docRef = doc(db, 'notifications', notificationId)

  await updateDoc(docRef, {
    isDismissed: true,
    dismissedAt: serverTimestamp(),
    isRead: true,
    readAt: serverTimestamp(),
  })
}

// ============================================
// DISMISS ALL
// ============================================

export async function dismissAllNotifications(
  userId: string,
  agencyId: string
): Promise<number> {
  let totalUpdated = 0

  while (true) {
    const q = query(
      collection(db, 'notifications'),
      where('agencyId', '==', agencyId),
      where('userId', '==', userId),
      where('isDismissed', '==', false),
      limit(NOTIFICATION_BATCH_LIMIT)
    )

    const snapshot = await getDocs(q)
    if (snapshot.empty) break

    const batch = writeBatch(db)
    snapshot.docs.forEach(notificationDoc => {
      batch.update(notificationDoc.ref, {
        isDismissed: true,
        dismissedAt: serverTimestamp(),
        isRead: true,
        readAt: serverTimestamp(),
      })
    })

    await batch.commit()
    totalUpdated += snapshot.size

    if (snapshot.size < NOTIFICATION_BATCH_LIMIT) break
  }

  return totalUpdated
}

// ============================================
// GET UNREAD COUNT
// ============================================

export async function getUnreadNotificationCount(
  userId: string,
  agencyId: string
): Promise<number> {
  const q = query(
    collection(db, 'notifications'),
    where('agencyId', '==', agencyId),
    where('userId', '==', userId),
    where('isRead', '==', false),
    where('isDismissed', '==', false)
  )

  const snapshot = await getDocs(q)
  return snapshot.size
}

// ============================================
// NOTIFICATION HELPERS
// ============================================

export function createVisitReminderNotification(params: {
  userId: string
  agencyId: string
  visitId: string
  clientName: string
  scheduledTime: string
}): Promise<string> {
  const { userId, agencyId, visitId, clientName, scheduledTime } = params

  return createNotification({
    userId,
    agencyId,
    type: 'visit_reminder',
    title: 'Upcoming Shift',
    body: `You have a shift with ${clientName} at ${scheduledTime}`,
    relatedEntity: { type: 'visit', id: visitId },
    actionUrl: `/schedule/${visitId}`,
  })
}

export function createShiftOpenNotification(params: {
  userId: string
  agencyId: string
  visitId: string
  clientName: string
  scheduledDate: string
}): Promise<string> {
  const { userId, agencyId, visitId, clientName, scheduledDate } = params

  return createNotification({
    userId,
    agencyId,
    type: 'shift_open',
    title: 'Open Shift Available',
    body: `An open shift is available for ${clientName} on ${scheduledDate}`,
    relatedEntity: { type: 'visit', id: visitId },
    actionUrl: `/open-shifts`,
    deliveryChannels: ['in_app', 'email'],
  })
}

export function createIncidentNotification(params: {
  userId: string
  agencyId: string
  incidentId: string
  clientName: string
  incidentType: string
  severity: string
}): Promise<string> {
  const { userId, agencyId, incidentId, clientName, incidentType, severity } = params

  return createNotification({
    userId,
    agencyId,
    type: 'incident_reported',
    title: `${severity.charAt(0).toUpperCase() + severity.slice(1)} Incident Reported`,
    body: `A ${incidentType} incident was reported for ${clientName}`,
    relatedEntity: { type: 'incident', id: incidentId },
    actionUrl: `/incidents/${incidentId}`,
    deliveryChannels: severity === 'critical' ? ['in_app', 'email', 'sms'] : ['in_app', 'email'],
  })
}

export function createAuthorizationExpiringNotification(params: {
  userId: string
  agencyId: string
  authorizationId: string
  clientName: string
  daysRemaining: number
}): Promise<string> {
  const { userId, agencyId, authorizationId, clientName, daysRemaining } = params

  return createNotification({
    userId,
    agencyId,
    type: 'authorization_expiring',
    title: 'Authorization Expiring Soon',
    body: `Authorization for ${clientName} expires in ${daysRemaining} days`,
    relatedEntity: { type: 'authorization', id: authorizationId },
    actionUrl: `/authorizations/${authorizationId}`,
  })
}

export function createCoverageNeededNotification(params: {
  userId: string
  agencyId: string
  visitId: string
  clientName: string
  scheduledDate: string
  hoursUntil: number
}): Promise<string> {
  const { userId, agencyId, visitId, clientName, scheduledDate, hoursUntil } = params

  return createNotification({
    userId,
    agencyId,
    type: 'coverage_needed',
    title: 'Coverage Needed',
    body: `Coverage needed for ${clientName} on ${scheduledDate} (${hoursUntil} hours away)`,
    relatedEntity: { type: 'visit', id: visitId },
    actionUrl: `/schedule/${visitId}`,
    deliveryChannels: hoursUntil <= 24 ? ['in_app', 'email', 'sms'] : ['in_app', 'email'],
  })
}

export function createPOCReviewNotification(params: {
  userId: string
  agencyId: string
  pocId: string
  clientName: string
  reviewDate: string
}): Promise<string> {
  const { userId, agencyId, pocId, clientName, reviewDate } = params

  return createNotification({
    userId,
    agencyId,
    type: 'poc_review',
    title: 'Plan of Care Review Due',
    body: `Plan of Care for ${clientName} is due for review on ${reviewDate}`,
    relatedEntity: { type: 'poc', id: pocId },
    actionUrl: `/forms/${pocId}`,
  })
}

export function createMessageNotification(params: {
  userId: string
  agencyId: string
  threadId: string
  senderName: string
  messagePreview: string
}): Promise<string> {
  const { userId, agencyId, threadId, senderName, messagePreview } = params

  return createNotification({
    userId,
    agencyId,
    type: 'message',
    title: `New message from ${senderName}`,
    body: messagePreview.length > 100 ? messagePreview.substring(0, 100) + '...' : messagePreview,
    relatedEntity: { type: 'message', id: threadId },
    actionUrl: `/messages/${threadId}`,
  })
}

export function createTimeOffRequestNotification(params: {
  userId: string
  agencyId: string
  requestId: string
  caregiverName: string
  startDate: string
  endDate: string
  type: string
}): Promise<string> {
  const { userId, agencyId, requestId, caregiverName, startDate, endDate, type } = params

  const dateRange = startDate === endDate ? startDate : `${startDate} - ${endDate}`

  return createNotification({
    userId,
    agencyId,
    type: 'system',
    title: 'Time Off Request',
    body: `${caregiverName} has requested ${type} time off for ${dateRange}`,
    relatedEntity: { type: 'user', id: requestId },
    actionUrl: `/time-off`,
  })
}

// ============================================
// SCHEDULE CONFLICT NOTIFICATIONS
// ============================================

export function createScheduleConflictOverlapNotification(params: {
  userId: string
  agencyId: string
  visitId: string
  clientName: string
  scheduledDate: string
  conflictDetails: string
}): Promise<string> {
  const { userId, agencyId, visitId, clientName, scheduledDate, conflictDetails } = params

  return createNotification({
    userId,
    agencyId,
    type: 'schedule_conflict_overlap',
    title: 'Schedule Conflict Detected',
    body: `Overlapping shifts detected for ${clientName} on ${scheduledDate}. ${conflictDetails}`,
    relatedEntity: { type: 'visit', id: visitId },
    actionUrl: `/schedule/${visitId}`,
    deliveryChannels: ['in_app', 'push'],
  })
}

export function createScheduleConflictTravelTimeNotification(params: {
  userId: string
  agencyId: string
  visitId: string
  clientName: string
  scheduledDate: string
  travelTime: number
  gapTime: number
}): Promise<string> {
  const { userId, agencyId, visitId, clientName, scheduledDate, travelTime, gapTime } = params

  return createNotification({
    userId,
    agencyId,
    type: 'schedule_conflict_travel_time',
    title: 'Insufficient Travel Time',
    body: `Only ${gapTime} min gap before shift with ${clientName} on ${scheduledDate}, but estimated travel is ${travelTime} min`,
    relatedEntity: { type: 'visit', id: visitId },
    actionUrl: `/schedule/${visitId}`,
    deliveryChannels: ['in_app'],
  })
}

export function createScheduleConflictMaxHoursNotification(params: {
  userId: string
  agencyId: string
  visitId: string
  scheduledDate: string
  totalHours: number
  maxHours: number
  period: 'day' | 'week'
}): Promise<string> {
  const { userId, agencyId, visitId, scheduledDate, totalHours, maxHours, period } = params

  return createNotification({
    userId,
    agencyId,
    type: 'schedule_conflict_max_hours',
    title: `Max ${period === 'day' ? 'Daily' : 'Weekly'} Hours Exceeded`,
    body: `Scheduled hours (${totalHours.toFixed(1)}h) exceed your max of ${maxHours}h per ${period} on ${scheduledDate}`,
    relatedEntity: { type: 'visit', id: visitId },
    actionUrl: `/schedule/${visitId}`,
    deliveryChannels: ['in_app'],
  })
}
